import { buildCollection, buildProperty } from "@firecms/core";
import { HomeButton } from "../components/HomeButton";

export type Challenge = {
    title: string;
    description: string;
    type: string;
    criteria: {
        sessions: number;
        roleplay_sessions: number;
        study_sessions: number;
        streak_days: number;
        xp_earned: number;
    };
    reward: {
        xp: number;
        text_credits: number;
        voice_credits: number;
        badge_id?: string;
    };
    start_date: Date;
    end_date: Date;
    is_active: boolean;
};

export const challengesCollection = buildCollection<Challenge>({
    id: "challenges",
    name: "Challenges",
    singularName: "Challenge",
    path: "challenges",
    icon: "EmojiEvents",
    group: "Gamification",
    description: "Weekly and monthly challenges with configurable criteria and rewards",
    Actions: HomeButton,
    permissions: {
        edit: true,
        create: true,
        delete: true,
    },
    properties: {
        title: buildProperty({
            name: "Title",
            dataType: "string",
            validation: { required: true },
        }),
        description: buildProperty({
            name: "Description",
            dataType: "string",
            multiline: true,
            validation: { required: true },
        }),
        type: buildProperty({
            name: "Type",
            dataType: "string",
            enumValues: {
                weekly: "📅 Weekly",
                monthly: "📆 Monthly",
            },
            validation: { required: true },
            defaultValue: "weekly",
        }),
        is_active: buildProperty({
            name: "Active",
            dataType: "boolean",
            defaultValue: true,
        }),
        start_date: buildProperty({
            name: "Start Date",
            dataType: "date",
            mode: "date_time",
            validation: { required: true },
        }),
        end_date: buildProperty({
            name: "End Date",
            dataType: "date",
            mode: "date_time",
            validation: { required: true },
        }),
        criteria: buildProperty<Challenge["criteria"]>({
            name: "Criteria",
            dataType: "map",
            description: "Set non-zero values for requirements. All non-zero criteria must be met.",
            properties: {
                sessions: buildProperty({
                    name: "Total Sessions",
                    dataType: "number",
                    defaultValue: 0,
                    description: "Complete N sessions (any type)",
                }),
                roleplay_sessions: buildProperty({
                    name: "Roleplay Sessions",
                    dataType: "number",
                    defaultValue: 0,
                }),
                study_sessions: buildProperty({
                    name: "Study Sessions",
                    dataType: "number",
                    defaultValue: 0,
                }),
                streak_days: buildProperty({
                    name: "Streak Days",
                    dataType: "number",
                    defaultValue: 0,
                }),
                xp_earned: buildProperty({
                    name: "XP Earned",
                    dataType: "number",
                    defaultValue: 0,
                }),
            },
        }),
        reward: buildProperty<Challenge["reward"]>({
            name: "Reward",
            dataType: "map",
            properties: {
                xp: buildProperty({
                    name: "XP Reward",
                    dataType: "number",
                    defaultValue: 0,
                }),
                text_credits: buildProperty({
                    name: "Text Credits",
                    dataType: "number",
                    defaultValue: 0,
                }),
                voice_credits: buildProperty({
                    name: "Voice Credits",
                    dataType: "number",
                    defaultValue: 0,
                }),
                badge_id: buildProperty({
                    name: "Badge ID",
                    dataType: "string",
                    description: "Optional badge to award on completion",
                }),
            },
        }),
    },
    propertiesOrder: [
        "title",
        "description",
        "type",
        "is_active",
        "start_date",
        "end_date",
        "criteria",
        "reward",
    ],
});
